import React, { useEffect, useState, useMemo, useCallback, useReducer, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import ReactDOMServer from "react-dom/server";
import { IconHandClick, IconArrowNarrowRight } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import unIntento from "../../assets/un-intento.png";
import dosIntentos from "../../assets/dos-intentos.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";
import "./Scenario3.css";

/** Clases extra del popup SweetAlert2 (tema burdeos Cobega). */
const SC3_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--cobega";
/** Pista: borde cian, avatar enmarcado, botón confirmar en teal (ver modals.css · modal-sg5--hint-sc2). */
const SC3_HINT_THEME_CLASS = `${SC3_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();

const SC3_SWAL_ICON_ARROW = ReactDOMServer.renderToStaticMarkup(
	<IconArrowNarrowRight size={50} color="#a41f35" stroke={2} />
);

const SC3_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM = `A lo largo de su historia, se ha lidiado con entornos económicos y geopolíticos cambiantes, y la familia ha sabido transformarse y adaptarse. Gracias al espíritu emprendedor y a su visión empresarial, son numerosas las aventuras empresariales en las que se han embarcado. A continuación, tienes una lista de los más significativos. ¿Serías capaz de ordenarlos cronológicamente?`;

const RETROALIMENTACION =
	"El orden correcto es: 1849 — comercio de tejidos de algodón; 1853 — importación de pesca salada; 1941 — jugos de frutos y posteriormente bebidas carbónicas; 1951 — concesión de Coca-Cola; 1990 — distribución integral de bebidas y alimentos en máquinas expendedoras; 1993 — concesión exclusiva de Nespresso; 2011 — Cacaolat; 2014 — Domino's Pizza.";

const HINT_POEM = `Del hilo nació el primer sendero,
del mar llegó el segundo secreto.
Luego el fruto se hizo burbuja,
y la botella marcó la ruta.
Después vinieron máquinas, aromas y sabores,
nuevas marcas, nuevos motores.
Seguid la metamorfosis del consumo,
y el tiempo revelará su rumbo.`;

const HINT_POEM_HTML = `<div class="txtTexto1 sc3-chrono-hint-poem">${HINT_POEM.replace(/\n/g, "<br/>")}</div>`;

/** Años fijos (orden cronológico); en cada fila solo se muestra el año; la actividad se arrastra encima. */
const CHRONOLOGY_ROWS = [
	{ year: 1849, activityId: "cotton", text: "Comercio de Tejidos de algodón" },
	{ year: 1853, activityId: "fish", text: "Importación de pesca salada" },
	{ year: 1941, activityId: "juices", text: "Jugos de frutos y posteriormente Bebidas Carbónicas" },
	{ year: 1951, activityId: "cocacola", text: "Concesión de Coca-Cola" },
	{
		year: 1990,
		activityId: "vending",
		text: "Distribución integral de bebidas y alimentos en máquinas expendedoras.",
	},
	{ year: 1993, activityId: "nespresso", text: "Concesión exclusiva de Nespresso" },
	{ year: 2011, activityId: "cacaolat", text: "Cacaolat" },
	{ year: 2014, activityId: "dominos", text: "Domino's Pizza" },
];

const ACTIVITY_TEXT = Object.fromEntries(CHRONOLOGY_ROWS.map((r) => [r.activityId, r.text]));

function initialPoolIds() {
	const items = CHRONOLOGY_ROWS.map((r) => ({ id: r.activityId, text: r.text }));
	return shuffleQuizOptions(items).map((o) => o.id);
}

function isOrderCorrect(slots) {
	return CHRONOLOGY_ROWS.every((row, i) => slots[i] === row.activityId);
}

function boardReducer(state, action) {
	const n = CHRONOLOGY_ROWS.length;
	switch (action.type) {
		case "POOL_TO_SLOT": {
			const { slotIndex, activityId } = action;
			if (slotIndex < 0 || slotIndex >= n) return state;
			if (!state.pool.includes(activityId)) return state;
			const nextSlots = [...state.slots];
			const displaced = nextSlots[slotIndex];
			nextSlots[slotIndex] = activityId;
			let nextPool = state.pool.filter((id) => id !== activityId);
			if (displaced && !nextPool.includes(displaced)) nextPool = [...nextPool, displaced];
			return { pool: nextPool, slots: nextSlots };
		}
		case "SLOT_TO_POOL": {
			const { slotIndex, activityId } = action;
			if (slotIndex < 0 || slotIndex >= n) return state;
			if (state.slots[slotIndex] !== activityId) return state;
			const nextSlots = [...state.slots];
			nextSlots[slotIndex] = null;
			const nextPool = state.pool.includes(activityId) ? state.pool : [...state.pool, activityId];
			return { pool: nextPool, slots: nextSlots };
		}
		case "SLOT_SWAP": {
			const { fromIdx, toIdx } = action;
			if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || fromIdx >= n || toIdx >= n) return state;
			const nextSlots = [...state.slots];
			const tmp = nextSlots[fromIdx];
			nextSlots[fromIdx] = nextSlots[toIdx];
			nextSlots[toIdx] = tmp;
			return { ...state, slots: nextSlots };
		}
		default:
			return state;
	}
}

function Scenario3Quiz1() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ2;
	const SCENARIO_ID = SCENARIOS.SCENARIO_3;

	const [board, dispatchBoard] = useReducer(boardReducer, undefined, () => ({
		pool: initialPoolIds(),
		slots: Array(CHRONOLOGY_ROWS.length).fill(null),
	}));
	const { pool, slots } = board;

	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [attempt, setAttempt] = useState(1);
	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	const dndLocked = isCompleted || isSubmitting;

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -2;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "violet");

	const allSlotsFilled = useMemo(() => slots.every(Boolean), [slots]);

	const dragSessionRef = useRef(null);
	const ghostElRef = useRef(null);
	const [dragGhost, setDragGhost] = useState(null);

	const positionGhostFromSession = useCallback(() => {
		const el = ghostElRef.current;
		const d = dragSessionRef.current;
		if (!el || !d?.ghostShown) return;
		const maxW = typeof window !== "undefined" ? Math.min(d.width, window.innerWidth * 0.92) : d.width;
		el.style.width = `${maxW}px`;
		el.style.minHeight = `${Math.max(d.height, 28)}px`;
		el.style.left = `${d.lastClientX - d.offX}px`;
		el.style.top = `${d.lastClientY - d.offY}px`;
	}, []);

	const endPointerDrag = useCallback((el, pointerId) => {
		try {
			if (el?.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId);
		} catch {
			/* ignore */
		}
		dragSessionRef.current = null;
		setDragGhost(null);
		document.body.classList.remove("sc3-chrono-dnd-active");
	}, []);

	const applyDropFromPayload = useCallback((payload, target) => {
		if (target.kind === "pool") {
			if (payload.source !== "slot") return;
			dispatchBoard({
				type: "SLOT_TO_POOL",
				slotIndex: payload.slotIndex,
				activityId: payload.activityId,
			});
			return;
		}
		const slotIndex = target.slotIndex;
		if (payload.source === "pool") {
			dispatchBoard({ type: "POOL_TO_SLOT", slotIndex, activityId: payload.activityId });
		} else if (payload.source === "slot") {
			dispatchBoard({ type: "SLOT_SWAP", fromIdx: payload.slotIndex, toIdx: slotIndex });
		}
	}, []);

	const resolveDropTarget = useCallback((clientX, clientY) => {
		const stack = document.elementsFromPoint(clientX, clientY);
		for (const node of stack) {
			if (!(node instanceof Element)) continue;
			if (node.closest(".sc3-chrono-drag-ghost")) continue;
			const poolEl = node.closest("[data-sc3-drop=\"pool\"]");
			if (poolEl) return { kind: "pool" };
			const row = node.closest("[data-sc3-slot]");
			if (row) {
				const raw = row.getAttribute("data-sc3-slot");
				const slotIndex = raw != null ? parseInt(raw, 10) : NaN;
				if (Number.isFinite(slotIndex)) return { kind: "slot", slotIndex };
			}
		}
		return null;
	}, []);

	const handleChipPointerDown = useCallback((e, payload) => {
		if (dndLocked) return;
		if (e.pointerType === "mouse" && e.button !== 0) return;
		const rect = e.currentTarget.getBoundingClientRect();
		dragSessionRef.current = {
			payload,
			startX: e.clientX,
			startY: e.clientY,
			pointerId: e.pointerId,
			moved: false,
			ghostShown: false,
			offX: e.clientX - rect.left,
			offY: e.clientY - rect.top,
			width: rect.width,
			height: rect.height,
			lastClientX: e.clientX,
			lastClientY: e.clientY,
		};
		e.currentTarget.setPointerCapture(e.pointerId);
		e.preventDefault();
	}, [dndLocked]);

	const handleChipPointerMove = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			d.lastClientX = e.clientX;
			d.lastClientY = e.clientY;
			const dx = e.clientX - d.startX;
			const dy = e.clientY - d.startY;
			if (dx * dx + dy * dy > 36) {
				d.moved = true;
				document.body.classList.add("sc3-chrono-dnd-active");
				if (!d.ghostShown) {
					d.ghostShown = true;
					const aid = d.payload.activityId;
					setDragGhost({
						activityId: aid,
						label: ACTIVITY_TEXT[aid] ?? aid,
						width: d.width,
						height: d.height,
					});
				}
			}
			if (d.ghostShown && ghostElRef.current) positionGhostFromSession();
		},
		[positionGhostFromSession]
	);

	useLayoutEffect(() => {
		if (!dragGhost) return;
		positionGhostFromSession();
	}, [dragGhost, positionGhostFromSession]);

	const handleChipPointerUpOrCancel = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			const el = e.currentTarget;
			const { payload, moved } = d;

			if (moved) {
				const target = resolveDropTarget(e.clientX, e.clientY);
				if (target) applyDropFromPayload(payload, target);
			}

			endPointerDrag(el, e.pointerId);
		},
		[applyDropFromPayload, endPointerDrag, resolveDropTarget]
	);

	useEffect(() => {
		return () => {
			document.body.classList.remove("sc3-chrono-dnd-active");
		};
	}, []);

	async function submitAnswer(finalScore, correct) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO3_FIN,
				correct,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO3_FIN);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO3_FIN);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_POEM_HTML,
				hintMarkHtml: SC3_HINT_MARK_HTML,
				color: "violet",
				big: false,
				themePopupClass: SC3_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			image: getRandomEstasseguroAvatar(),
			imageHintUsed,
			hint: HINT_POEM_HTML,
			hintMarkHtml: SC3_HINT_MARK_HTML,
			color: "violet",
			big: false,
			themePopupClass: SC3_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function checkAnswer() {
		if (!allSlotsFilled) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 violet">Debes colocar todos los elementos</div>`,
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "violet",
			themePopupClass: SC3_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const correct = isOrderCorrect(slots);

			if (correct) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = base - hintThisAttempt;
				let newScore = pendingScore + attemptDelta;

				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const capitalInicio = capitalAtQuizStartRef.current ?? 0;
				const netoEnEstaPregunta = newScore - capitalInicio;

				modalQuestionResolution({
					correct: true,
					question: "Orden cronológico",
					textHtml: `${buildQuizResultAvatarHtml(getRandomAciertoAvatar(), "correct", "orange")}
                      <div class="txt28 bold pt-2">Has ganado ${attemptDelta} M€ en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                      ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                      <div class="txt20 mt-2">Ganancia en esta pregunta: <b>${netoEnEstaPregunta}</b> M€</div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				}).then(() => {
					submitAnswer(newScore, true);
				});

				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				let newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const capitalInicio = capitalAtQuizStartRef.current ?? 0;
				const netoEnEstaPregunta = newScore - capitalInicio;

				modalQuestionResolution({
					correct: false,
					question: "Orden cronológico",
					textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "violet")}
                      <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} M€${hintThisAttempt ? " (incluye -3M€ por pista)" : ""}</div>
                      <div class="txt22 mt-2">Te queda <b>1</b> intento. Reordena los elementos y vuelve a comprobar.</div>
                      <div class="txt20 mt-2">Ganancia en esta pregunta: <b>${netoEnEstaPregunta}</b> M€</div>
                      <div class="txt18 mt-2 sc2-swal-hint-actions">Pulsa la flecha para seguir intentando.</div>`,
					customConfirmButtonHtml: SC3_SWAL_ICON_ARROW,
					confirmIsIconButton: true,
					color: "violet",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				}).then(() => {
					setPendingScore(newScore);
					setAttempt((a) => a + 1);
					setIsSubmitting(false);
				});

				return;
			}

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			let newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			const capitalInicio = capitalAtQuizStartRef.current ?? 0;
			const netoEnEstaPregunta = newScore - capitalInicio;

			modalQuestionResolution({
				correct: false,
				question: "Orden cronológico",
				textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "violet")}
                    <div class="txt28 bold pt-4">Han agotado los intentos</div>
                    ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3M€ por pista en este intento</div>` : ""}
                    <div class="txt20 mt-2">Ganancia en esta pregunta: <b>${netoEnEstaPregunta}</b> M€</div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "violet",
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc3-chrono-quiz">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero">
						<div className="sc3-chrono-hero-head">
							<h1 className="sg5-title">Pregunta</h1>
							<span className="start-game-badge sg5-badge sc3-chrono-hero-badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 3 · Evolución
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem sc3-chrono-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar sc3-chrono-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								Mantén pulsado una actividad, muévela y suéltala sobre el año correspondiente (o sobre la
								zona inferior para devolverla).
							</span>
						</p>
						<img
							src={remainingAttempts === 2 ? dosIntentos : unIntento}
							alt=""
							className="sc2-quiz-intentos"
						/>
					</div>

					<div className="sc3-chrono-layout">
						<div className="sc3-chrono-table" aria-label="Tabla de años">
							{Array.from({ length: Math.ceil(CHRONOLOGY_ROWS.length / 2) }, (_, pairRowIndex) => {
								const start = pairRowIndex * 2;
								return (
									<div key={CHRONOLOGY_ROWS[start].year} className="sc3-chrono-table-row-double">
										{[start, start + 1].map((slotIndex) => {
											if (slotIndex >= CHRONOLOGY_ROWS.length) {
												return (
													<div
														key={`pad-${slotIndex}`}
														className="sc3-chrono-pair sc3-chrono-pair--placeholder"
														aria-hidden
													/>
												);
											}
											const row = CHRONOLOGY_ROWS[slotIndex];
											const placedId = slots[slotIndex];
											return (
												<div key={row.year} className="sc3-chrono-pair" data-sc3-slot={slotIndex}>
													<div className="sc3-chrono-year">{row.year}</div>
													<div
														className={`sc3-chrono-slot ${placedId ? "sc3-chrono-slot--filled" : "sc3-chrono-slot--empty"}`}
													>
														{placedId ? (
															<div
																tabIndex={0}
																className={`sc3-chrono-chip ${dragGhost?.activityId === placedId ? "sc3-chrono-chip--drag-source" : ""}`}
																onPointerDown={(e) =>
																	handleChipPointerDown(e, {
																		source: "slot",
																		activityId: placedId,
																		slotIndex,
																	})
																}
																onPointerMove={handleChipPointerMove}
																onPointerUp={handleChipPointerUpOrCancel}
																onPointerCancel={handleChipPointerUpOrCancel}
																onLostPointerCapture={(e) => {
																	if (dragSessionRef.current?.pointerId === e.pointerId) {
																		endPointerDrag(e.currentTarget, e.pointerId);
																	}
																}}
																onContextMenu={(ev) => ev.preventDefault()}
																title={ACTIVITY_TEXT[placedId]}
															>
																{ACTIVITY_TEXT[placedId]}
															</div>
														) : (
															<span className="sc3-chrono-slot-placeholder" aria-hidden="true">
																Soltar aquí
															</span>
														)}
													</div>
												</div>
											);
										})}
									</div>
								);
							})}
						</div>

						<div className="sc3-chrono-pool-wrap">
							<p className="sc3-chrono-pool-label mb-2">Actividades (arrastra desde aquí)</p>
							<div
								className="sc3-chrono-pool"
								data-sc3-drop="pool"
								aria-label="Zona de actividades sin colocar"
							>
								{pool.length === 0 ? (
									<span className="sc3-chrono-pool-empty">Todas las fichas están colocadas</span>
								) : (
									pool.map((activityId) => (
										<div
											key={activityId}
											tabIndex={0}
											className={`sc3-chrono-chip ${dragGhost?.activityId === activityId ? "sc3-chrono-chip--drag-source" : ""}`}
											onPointerDown={(e) => handleChipPointerDown(e, { source: "pool", activityId })}
											onPointerMove={handleChipPointerMove}
											onPointerUp={handleChipPointerUpOrCancel}
											onPointerCancel={handleChipPointerUpOrCancel}
											onLostPointerCapture={(e) => {
												if (dragSessionRef.current?.pointerId === e.pointerId) {
													endPointerDrag(e.currentTarget, e.pointerId);
												}
											}}
											onContextMenu={(ev) => ev.preventDefault()}
											title={ACTIVITY_TEXT[activityId]}
										>
											{ACTIVITY_TEXT[activityId]}
										</div>
									))
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3 M€)" : "Usar pista (-3 M€)"}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? <span className="badge-penalty sc2-quiz-hint-badge">-3M€</span> : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />

			{typeof document !== "undefined" &&
				dragGhost &&
				createPortal(
					<div
						ref={ghostElRef}
						className="sc3-chrono-drag-ghost"
						role="presentation"
						aria-hidden="true"
					>
						{dragGhost.label}
					</div>,
					document.body
				)}
		</div>
	);
}

export default Scenario3Quiz1;
